import React, { useState, useRef, useEffect } from "react";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Modal,
  TextInput,
  ScrollView,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../Firestore/FirestoreConfig";

import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import GlobalStyles from "../../Resources/Styles";
import { useScreenDimensions, useDevice } from "../_layout";
import CustomActivityIndicator from "./CustomActivityIndicator";
import { useToast } from "../contexts/ToastContext";
import AthleteAppOnboardingModal from "./AthleteAppOnboardingModal";

const AddNewClientView = ({
  isVisible,
  onClose,
  coach,
  setShowingMaxClientsTrial,
  setShowingMaxClientsReached,
}) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { showToast } = useToast();

  const [emails, setEmails] = useState([]);
  const [currentInput, setCurrentInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [inputError, setInputError] = useState(false);
  const [invitationErrors, setInvitationErrors] = useState({
    alreadyInvited: [],
    alreadyHasCoach: [],
    sendErrors: [],
  });
  const [closeButtonHovered, setCloseButtonHovered] = useState(false);
  const [showAthleteAppPreview, setShowAthleteAppPreview] = useState(false);
  const [hoveredMetaLink, setHoveredMetaLink] = useState(null);

  const inputRef = useRef(null);

  // Focus input when modal becomes visible
  useEffect(() => {
    if (isVisible && inputRef.current) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isVisible]);

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const addEmail = () => {
    const trimmedInput = currentInput.trim();
    if (!trimmedInput) return;

    if (validateEmail(trimmedInput)) {
      if (!emails.includes(trimmedInput.toLowerCase())) {
        setEmails([...emails, trimmedInput.toLowerCase()]);
      }
      setCurrentInput("");
      setInputError(false);
      setInvitationErrors({
        alreadyInvited: [],
        alreadyHasCoach: [],
        sendErrors: [],
      });
    } else {
      setInputError(true);
    }
  };

  const removeEmail = (index) => {
    const newEmails = [...emails];
    newEmails.splice(index, 1);
    setEmails(newEmails);
  };

  const handleKeyPress = (e) => {
    if (
      e.nativeEvent.key === "Enter" ||
      e.nativeEvent.key === " " ||
      e.nativeEvent.key === ","
    ) {
      addEmail();
    }
    if (
      e.nativeEvent.key === "Backspace" &&
      currentInput === "" &&
      emails.length > 0
    ) {
      removeEmail(emails.length - 1);
    }
  };

  const sendInvitations = async () => {
    let emailList = [...emails];
    if (currentInput) {
      const trimmedInput = currentInput.trim();
      if (validateEmail(trimmedInput)) {
        emailList.push(trimmedInput.toLowerCase());
      } else {
        setInputError(true);
        return;
      }
    }

    if (emailList.length === 0) return;

    await processInvitations(emailList);
  };

  const processInvitations = async (emailList) => {
    if (coach.clients.length + emailList.length > coach.maxClients) {
      onClose();
      if (coach.subscriptionStatus === "trialing") {
        setShowingMaxClientsTrial();
      } else {
        setShowingMaxClientsReached();
      }
      return;
    }

    setIsLoading(true);
    setInvitationErrors({
      alreadyInvited: [],
      alreadyHasCoach: [],
      sendErrors: [],
    });

    try {
      const results = await coach.newClientInvitation(emailList, showToast);

      // Show success toast if any were sent
      if (results.sent.length > 0) {
        if (results.sent.length === 1) {
          showToast("invitationSent-string", {
            type: "success",
            description: "invitationSentDescription-string",
          });
        } else {
          showToast("invitationsSent-string", {
            type: "success",
            description: "invitationsSentDescription-string",
            variables: {
              count: results.sent.length,
            },
          });
        }
      }

      // Remove successfully sent emails from the list
      if (results.sent.length > 0) {
        const remainingEmails = emails.filter(
          (email) => !results.sent.includes(email)
        );
        setEmails(remainingEmails);

        // Clear currentInput if it was successfully sent
        if (
          currentInput &&
          results.sent.includes(currentInput.trim().toLowerCase())
        ) {
          setCurrentInput("");
        }
      }

      // If all succeeded, close modal
      const totalErrors =
        results.alreadyInvited.length +
        results.alreadyHasCoach.length +
        results.errors.length;

      if (totalErrors === 0 && results.sent.length > 0) {
        onClose();
      } else {
        // Show errors in UI
        setInvitationErrors({
          alreadyInvited: results.alreadyInvited,
          alreadyHasCoach: results.alreadyHasCoach,
          sendErrors: results.errors.map((e) => e.email),
        });
      }
    } catch (error) {
      console.error("Error sending invitations:", error);
      setInvitationErrors({
        alreadyInvited: [],
        alreadyHasCoach: [],
        sendErrors: emailList,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const pdfRef = ref(
        storage,
        i18n.language === "es"
          ? "documents/guia_uso_efort_app.pdf"
          : "documents/user_guide_efort_app.pdf"
      );
      const url = await getDownloadURL(pdfRef);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={!isLoading && onClose}
    >
      <View style={GlobalStyles.centeredView}>
        <TouchableOpacity
          onPress={() => {
            if (emails.length === 0 && !currentInput) {
              onClose();
            }
          }}
          style={GlobalStyles.touchableBackground}
          disabled={isLoading || emails.length > 0 || currentInput}
        />
        <View
          style={[
            styles.modalView,
            {
              width: 580,
              maxWidth: "90%",
              flexDirection: "column",
              maxHeight: "90%",
            },
          ]}
        >
          {/* Close Button */}
          <TouchableOpacity
            style={[styles.closeButton]}
            onPress={onClose}
            disabled={isLoading}
            onMouseEnter={() => !isMobile && setCloseButtonHovered(true)}
            onMouseLeave={() => setCloseButtonHovered(false)}
          >
            <Ionicons
              name="close"
              size={24}
              color={closeButtonHovered ? Colors.blackColor : Colors.grayColor}
            />
          </TouchableOpacity>

          {/* Left Side */}
          <View
            style={[
              styles.leftSide,
              {
                marginBottom: 20,
              },
            ]}
          >
            <Text style={styles.title}>
              {t("inviteAthlete-string").toUpperCase()}
            </Text>
            <Text style={styles.subtitle}>
              {t("inviteAthleteSubtitle-string")}
            </Text>
            <View style={styles.metaLinks}>
              <TouchableOpacity
                style={[
                  styles.metaLink,
                  hoveredMetaLink === "preview" && styles.metaLinkHovered,
                ]}
                onPress={() => setShowAthleteAppPreview(true)}
                onMouseEnter={() => !isMobile && setHoveredMetaLink("preview")}
                onMouseLeave={() => setHoveredMetaLink(null)}
              >
                <Ionicons name="play-outline" size={13} color="#6b7280" />
                <Text style={styles.metaLinkText}>
                  {t("previewAthleteApp-string")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.metaLink,
                  hoveredMetaLink === "guide" && styles.metaLinkHovered,
                ]}
                onPress={downloadPDF}
                onMouseEnter={() => !isMobile && setHoveredMetaLink("guide")}
                onMouseLeave={() => setHoveredMetaLink(null)}
              >
                <Ionicons name="download-outline" size={13} color="#6b7280" />
                <Text style={styles.metaLinkText}>
                  {t("athleteUserGuide-string")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Right Side */}
          <View style={[styles.rightSide, { flex: 1, marginTop: 8 }]}>
            <View style={styles.emailLabelRow}>
              <Text style={styles.emailLabel}>
                {t("athleteEmailAddresses-string")}
              </Text>
              <Text style={styles.emailHint}>{t("typeEmailHint-string")}</Text>
            </View>

            <View
              style={[
                styles.emailMultiInput,
                inputError && { borderColor: "red" },
              ]}
            >
              <ScrollView
                contentContainerStyle={styles.emailListContent}
                style={{ maxHeight: 200 }}
              >
                {emails.map((email, index) => (
                  <View key={index} style={styles.emailPill}>
                    <Text style={styles.emailPillText}>{email}</Text>
                    <TouchableOpacity onPress={() => removeEmail(index)}>
                      <Text style={styles.removeIcon}>×</Text>
                    </TouchableOpacity>
                  </View>
                ))}
                <TextInput
                  ref={inputRef}
                  style={[styles.emailInput, { fontSize: isMobile ? 16 : 13 }]}
                  value={currentInput}
                  onChangeText={(text) => {
                    setInputError(false);
                    setCurrentInput(text);
                  }}
                  placeholder={
                    emails.length === 0 ? t("emailPlaceholder-string") : ""
                  }
                  placeholderTextColor="#9ca3af"
                  onSubmitEditing={addEmail}
                  onKeyPress={handleKeyPress}
                  blurOnSubmit={false}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </ScrollView>
            </View>
            {inputError && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>
                  {t("invalidEmailFormat-string")}
                </Text>
              </View>
            )}

            {invitationErrors.alreadyInvited.length > 0 && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>
                  {t("alreadyInvited-string")}:{" "}
                  {invitationErrors.alreadyInvited.join(", ")}
                </Text>
              </View>
            )}

            {invitationErrors.alreadyHasCoach.length > 0 && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>
                  {t("alreadyHasCoach-string")}:{" "}
                  {invitationErrors.alreadyHasCoach.join(", ")}
                </Text>
              </View>
            )}

            {invitationErrors.sendErrors.length > 0 && (
              <View style={styles.errorContainer}>
                <Text style={styles.errorText}>
                  {t("errorSendingInvitations-string")}:{" "}
                  {invitationErrors.sendErrors.join(", ")}
                </Text>
              </View>
            )}

            <View style={styles.sendRow}>
              <TouchableOpacity
                style={[
                  styles.primaryButton,
                  (emails.length === 0 &&
                    (!currentInput || !validateEmail(currentInput.trim()))) ||
                  inputError
                    ? { opacity: 0.5 }
                    : {},
                ]}
                onPress={sendInvitations}
                disabled={
                  isLoading ||
                  (emails.length === 0 &&
                    (!currentInput || !validateEmail(currentInput.trim()))) ||
                  inputError
                }
              >
                {isLoading ? (
                  <CustomActivityIndicator large={false} white={true} />
                ) : (
                  <Text style={styles.primaryButtonText}>
                    {t("sendInvitations-string")}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>

      {/* Athlete App Preview Modal */}
      {showAthleteAppPreview && (
        <AthleteAppOnboardingModal
          isVisible={showAthleteAppPreview}
          onClose={() => setShowAthleteAppPreview(false)}
          onCompleted={() => setShowAthleteAppPreview(false)}
          isPreviewMode={true}
        />
      )}
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    padding: 24,
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  leftSide: {
    justifyContent: "flex-start",
  },
  rightSide: {
    gap: 12,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 8,
    color: Colors.blackColor,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 21,
    color: Colors.darkgrayColor,
    // color: "#4b5563",
    marginBottom: 16,
  },
  metaLinks: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  metaLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 9,
    backgroundColor: "#f3f4f6",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.borderColor,
  },
  metaLinkHovered: {
    backgroundColor: "#e5e7eb",
    borderColor: "#d1d5db",
  },
  metaLinkText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  emailLabelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 0,
    gap: 8,
  },
  emailLabel: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  emailHint: {
    fontSize: 11,
    color: Colors.grayColor,
  },
  emailMultiInput: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    minHeight: 40,
    backgroundColor: Colors.whiteColor,
    justifyContent: "center",
    overflow: "hidden",
    flexShrink: 1,
  },
  emailListContent: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    padding: 8,
  },
  emailPill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 10,
    backgroundColor: "rgba(63, 123, 141, 0.1)",
  },
  emailPillText: {
    fontSize: 11,
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  removeIcon: {
    fontSize: 13,
    color: Colors.bubbleColor,
    opacity: 0.5,
  },
  emailInput: {
    flex: 1,
    minWidth: 140,
    fontSize: 13,
    paddingVertical: 4,
    paddingHorizontal: 2,
    color: Colors.blackColor,
    outlineStyle: "none",
  },
  sendRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 12,
  },
  primaryButton: {
    backgroundColor: Colors.bubbleColor,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    height: 35,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.whiteColor,
  },
  errorContainer: {
    width: "100%",

    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: "#fee",
    borderRadius: 10,
    borderLeftWidth: 4,
    borderLeftColor: "#f56565",
  },
  errorText: {
    fontSize: 12,
    color: "#c53030",
    textAlign: "center",
  },
});

export default AddNewClientView;
